// frontend/src/services/theme.ts

export type ThemeMode = 'auto' | 'off';

const STORAGE_KEY = 'theme.auto';
const HTML_DARK_CLASS = 'dark';
const BODY_DARK_CLASS = 'app-theme-dark';

type ThemeDetail = { mode: ThemeMode; dark: boolean };

let listeners: Array<(mode: ThemeMode) => void> = [];

/** Aplica/quita todas las marcas necesarias para el tema oscuro. */
function applyDark(enable: boolean): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  const body = document.body;

  // Clases heredadas por el proyecto
  html.classList.toggle(HTML_DARK_CLASS, enable);
  body.classList.toggle(BODY_DARK_CLASS, enable);

  // Atributo estándar opcional
  try {
    html.setAttribute('data-theme', enable ? 'dark' : 'light');
  } catch {
    /* no-op */
  }
}

/** Notifica a la app (y a cualquier listener externo) que el tema cambió. */
function notify(mode: ThemeMode, dark: boolean): void {
  // Callbacks locales registrados vía onThemeChange
  for (const fn of [...listeners]) {
    try {
      fn(mode);
    } catch {
      /* no-op */
    }
  }

  // CustomEvent opcional (solo si window existe)
  if (typeof window !== 'undefined' && typeof (window as any).dispatchEvent === 'function') {
    try {
      const evt = new (window as any).CustomEvent('themechange', {
        detail: { mode, dark } as ThemeDetail,
      });
      (window as any).dispatchEvent(evt);
    } catch {
      /* no-op */
    }
  }
}

/** Devuelve el modo guardado. */
export function getMode(): ThemeMode {
  if (typeof window === 'undefined') return 'off';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'true' ? 'auto' : 'off';
  } catch {
    return 'off';
  }
}

/** Fija el modo y aplica marcas visuales. */
export function setMode(mode: ThemeMode): void {
  const enabled = mode === 'auto';
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, String(enabled));
    }
  } catch {
    /* no-op storage */
  }
  applyDark(enabled);
  notify(mode, enabled);
}

/** Atajo desde el switch del Perfil. */
export function setAutoDark(enabled: boolean): void {
  setMode(enabled ? 'auto' : 'off');
}

/** Inicializa el tema al montar la app y escucha cambios entre pestañas. */
export function initTheme(): void {
  try {
    const mode = getMode();
    const enabled = mode === 'auto';
    applyDark(enabled);
    notify(mode, enabled);
  } catch {
    applyDark(false);
    notify('off', false);
  }

  // Sincroniza entre pestañas/ventanas
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const mode = getMode();
      const enabled = mode === 'auto';
      applyDark(enabled);
      notify(mode, enabled);
    });
  }
}

/** Suscripción a cambios del tema (por código). Devuelve un unsubscribe seguro. */
export function onThemeChange(cb: (mode: ThemeMode) => void): () => void {
  listeners.push(cb);

  let handler: ((ev: Event) => void) | null = null;

  if (typeof window !== 'undefined') {
    handler = (ev: Event) => {
      // Evitar dependencia estricta de tipos DOM
      const anyEv = ev as any;
      const detail: ThemeDetail | undefined = anyEv?.detail;
      cb(detail?.mode ?? getMode());
    };
    try {
      window.addEventListener('themechange', handler as EventListener);
    } catch {
      /* no-op */
    }
  }

  return () => {
    listeners = listeners.filter((fn) => fn !== cb);
    if (handler && typeof window !== 'undefined') {
      try {
        window.removeEventListener('themechange', handler as EventListener);
      } catch {
        /* no-op */
      }
    }
  };
}
